import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import './Login.css'; // Import the CSS file
import { Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import Cookies from 'js-cookie';
import API_BASE_URL from '../../config/api';

const Login = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        email: '',
        password: ''
    });
    const [loading, setLoading] = useState(false);
    const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
    const [resetEmail, setResetEmail] = useState('');
    const [resetLoading, setResetLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    useEffect(() => {
        // Use cookie for token
        const token = Cookies.get('token');
        const userRole = Cookies.get('userRole');

        if (token && userRole) {
            switch (userRole) {
                case 'overall_manager':
                    navigate('/admin/dashboard');
                    break;
                case 'admin_manager':
                    navigate('/hr-manager/dashboard');
                    break;
                case 'immediate_manager':
                case 'md':
                    navigate('/manager/dashboard');
                    break;
                case 'staff':
                    navigate('/employee/dashboard');
                    break;
                default:
                    break;
            }
        }
    }, [navigate]);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const response = await axios.post(`${API_BASE_URL}/api/v1/users/login/`, formData);
            console.log(response.data);
            // Set token in cookie (expires in 7 days)
            Cookies.set('token', response.data.access, { expires: 7, path: '/' });
            Cookies.set('userRole', response.data.user.role);
            Cookies.set('employeeId', response.data.user.id);
            Cookies.set('currency', response.data.company?.currency);
            Cookies.set('subscribedModules', JSON.stringify(response.data.company?.subscribed_modules || []), { expires: 7, path: '/' });
            Cookies.set('user', JSON.stringify(response.data.user), { expires: 7, path: '/' });
            Cookies.set('company_name', response.data.company?.trade_name);

            Swal.fire({
                icon: 'success',
                title: 'Success!',
                text: 'Login successful',
                timer: 1500
            });

            // Check if it's user's first login
            if (response.data.user.first_login) {
                switch (response.data.user.role) {
                    case 'hr_manager':
                        navigate('/hr/change-password', {
                            state: {
                                oldPassword: response.data.user.employee_id
                            }
                        });
                        break;

                    case 'immediate_manager':
                        navigate('/manager/change-password', {
                            state: {
                                oldPassword: response.data.user.employee_id
                            }
                        });
                        break;
                    case 'staff':
                        navigate('/employee/change-password', {
                            state: {
                                oldPassword: response.data.user.employee_id
                            }
                        });
                        break;
                    default:
                        handleNormalLogin(response.data.user.role);
                        break;
                }
            } else {
                handleNormalLogin(response.data.user.role);
            }
        } catch (error) {
            console.log(error)
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.response?.data?.detail || 'Login failed'
            });
        } finally {
            setLoading(false);
        }
    };

    // Helper function to handle normal login redirects
    const handleNormalLogin = (role) => {
        switch (role) {
            case 'overall_manager':
                navigate('/admin/dashboard');
                break;
            case 'hr_manager':
                navigate('/hr-manager/dashboard');
                break;
            case 'immediate_manager':
            case 'md':
                navigate('/manager/dashboard');
                break;
            case 'staff':
                navigate('/employee/dashboard');
                break;
            default:
                navigate('/');
                break;
        }
    };

    const handleForgotPassword = async (e) => {
        e.preventDefault();
        setResetLoading(true);

        try {
            const response = await axios.post(`${API_BASE_URL}/api/v1/users/forgot-password/`, {
                email: resetEmail
            });

            Swal.fire({
                icon: 'success',
                title: 'Success!',
                text: 'Password reset instructions have been sent to your email',
                timer: 3000
            });

            setForgotPasswordOpen(false);
            setResetEmail('');
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.response?.data?.message || 'Failed to process password reset request'
            });
        } finally {
            setResetLoading(false);
        }
    };

    return (
        <div className="login-container bg-gray-100">
            <div className="login-form bg-white shadow-lg">
                <div className="logo-container">
                    <img src='/assets/images/burray/burray.png' alt='burrayhr' className="logo" />
                </div>
                <h4 style={{ textAlign: 'center', color: 'navy', marginBottom: '25px' }} className="text-center text-gray-600">Burray HR System</h4>
                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label htmlFor="email">Email</label>
                        <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            className="form-input"
                        />
                    </div>
                    <div className="form-group">
                        <label htmlFor="password">Password</label>
                        <div className="password-input-container">
                            <input
                                type={showPassword ? "text" : "password"}
                                id="password"
                                name="password"
                                value={formData.password}
                                onChange={handleChange}
                                required
                                className="form-input"
                            />
                            <button
                                type="button"
                                className="password-toggle-btn"
                                onClick={() => setShowPassword(!showPassword)}
                            >
                                {showPassword ? <FaEyeSlash /> : <FaEye />}
                            </button>
                        </div>
                    </div>
                    <button
                        type="submit"
                        disabled={loading}
                        className="login-button"
                    >
                        {loading ? 'Logging in...' : 'Login'}
                    </button>
                    <div className="forgot-password-link">
                        <button
                            type="button"
                            onClick={() => setForgotPasswordOpen(true)}
                            className="text-blue-600 hover:text-blue-800 text-sm"
                            style={{ color: 'navy', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', marginTop: '20px' }}
                        >
                            Forgot Password?
                        </button>
                    </div>
                </form>
            </div>

            {/* Forgot Password Modal */}
            <Dialog
                open={forgotPasswordOpen}
                onClose={() => setForgotPasswordOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Reset Password</DialogTitle>
                <DialogContent>
                    <div style={{ marginTop: '20px' }}>
                        <p style={{ marginBottom: '20px' }}>
                            Enter your email address and we'll send you instructions to reset your password.
                        </p>
                        <div className="form-group">
                            <label htmlFor="reset-email">Email</label>
                            <input
                                type="email"
                                id="reset-email"
                                value={resetEmail}
                                onChange={(e) => setResetEmail(e.target.value)}
                                required
                                className="form-input"
                                style={{ width: '100%' }}
                            />
                        </div>
                    </div>
                </DialogContent>
                <DialogActions style={{ padding: '20px' }}>
                    <Button
                        onClick={() => setForgotPasswordOpen(false)}
                        style={{ color: '#666' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleForgotPassword}
                        disabled={resetLoading || !resetEmail}
                        style={{
                            backgroundColor: '#1e3a8a',
                            color: 'white',
                            '&:hover': {
                                backgroundColor: '#1e40af'
                            }
                        }}
                    >
                        {resetLoading ? 'Sending...' : 'Send Reset Instructions'}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default Login;
